<script>
/* global google */

import { MapElementFactory } from 'vue2-google-maps'
import { mapGetters } from 'vuex'
import { scaleLinear } from 'd3-scale'
import statFun from '../api/stats'
import CustomOverlay from '../classes/USGSOverlay.js'

export default MapElementFactory({
  $_MapDataLayer_records: null,
  $_MapDataLayer_recordsByYear: null,

  name: 'MapDataLayer',

  ctr: () => google.maps.Data,

  events: [],

  mappedProps: {
    options: { type: Object }
  },

  props: {},

  beforeCreate (options) {
    // Hide borders until choropleth or kriging data is available
    // This avoids drawing gray areas with black borders while data is loading
    this.$map.data.setStyle({
      fillColor: 'transparent',
      strokeWeight: 0
    })
  },

  afterCreate (dataInstance) {
    this.readyMap = true

    this.$map.data.addListener('click', event => {
      var region = {
        unos_id: event.feature.getProperty('UNOS_ID'),
        opo_id: event.feature.getProperty('OPO_ID') || false,
        state_id: event.feature.getProperty('STATE_ID') || false
      }
      this.$store.commit('selectHospital', null)
      this.$store.commit('selectTxCenter', null)
      this.$store.commit('selectRegion', region)
    })

    this.$map.data.addListener('mouseover', event => {
      let value = this.aggregatedRecords[event.feature.getProperty(this.getGroupId())]
      this.$store.commit('setLegendHover', value)
    })

    this.$map.data.addListener('mousemove', event => {
      if (this.overlayKrig) {
        let index = this.overlayKrig.getColorAt(event)
        if (index >= 0) {
          this.$store.commit('setLegendIntervalHover', index)
        }
      }
    })
  },

  data: () => {
    return {
      overlayKrig: null,

      /** Will hold the results of aggregation based on this.query **/
      aggregatedRecords: [],

      /** true if Google Maps JS library is loaded */
      readyMap: false
    }
  },

  computed: {
    ...mapGetters({
      hospitalRecords: 'hospitalRecords',
      legendMin: 'legendMin',
      legendMax: 'legendMax',
      markersSource: 'markersSource',
      opoShapes: 'opoShapes',
      overlayMode: 'overlayMode',
      queryOrganMode: 'queryOrganMode',
      queryRegionMode: 'queryRegionMode',
      queryStatisticId: 'queryStatisticId',
      queryStatisticSubId: 'queryStatisticSubId',
      queryYear: 'queryYear',
      selectedStatistic: 'selectedStatistic',
      stateShapes: 'stateShapes',
      txRecords: 'txRecords',
      txCenters: 'txCenters',
      unosShapes: 'unosShapes'
    }),

    /**
     * Checks if everything is loaded.
     */
    ready () {
      return this.readyMap && this.readyRecords && this.readyShapes && this.readyQuery
    },

    /**
     * Checks if statistics have been loaded and query is set to default values.
     */
    readyQuery () {
      return this.queryOrganMode && this.queryRegionMode && this.selectedStatistic && this.queryYear
    },

    /**
     * Checks if data needed for statistics has been loaded.
     */
    readyRecords () {
      return this.txRecords.length > 0 && this.hospitalRecords.length > 0
    },

    /**
     * Check if GeoJSON shapes have been loaded.
     */
    readyShapes () {
      return this.opoShapes && this.unosShapes && this.stateShapes
    }
  },

  watch: {
    aggregatedRecords: function () {
      this.updateMinMax()
      this.updateRegionsStyle()
    },

    overlayMode: function () {
      if (this.overlayKrig) {
        if (this.overlayMode === 'k') {
          this.overlayKrig.show()
        } else {
          this.overlayKrig.hide()
        }
      }

      this.updateRegionsStyle()
    },

    /**
     * When all data has been loaded, update everything.
     */
    ready: function () {
      this.updateShapes()
    },

    queryOrganMode: function () { this.updateMap() },
    queryRegionMode: function () { this.updateShapes() },
    queryStatisticId: function () { this.updateMap() },
    queryStatisticSubId: function () { this.updateMap() },
    queryYear: function () { this.updateMap() },

    /**
     * Ensure that shapes are drawn as soon as they have been loaded.
     */
    readyShapes: function () {
      this.updateShapes()
    }
  },

  methods: {
    getGroupId () {
      switch (this.queryRegionMode) {
        case 'opo':
          return 'OPO_ID'
        case 'state':
          return 'STATE_ID'
        case 'unos':
          return 'UNOS_ID'
      }
    },

    doOverlay () {
      let overlayUrl = './static/km/' + (this.queryStatisticSubId || this.queryStatisticId) + '/' +
        'stat_' + (this.queryStatisticSubId || this.queryStatisticId) +
        '_' + this.queryYear +
        '_opo' +
        '_single' +
        '.png'

      if (this.overlayKrig) {
        this.overlayKrig.setImageUrl(overlayUrl)
      } else {
        let USGSOverlay = CustomOverlay.doClass()
        let bounds = new google.maps.LatLngBounds(
          new google.maps.LatLng(25.3461, -124.7002),
          new google.maps.LatLng(49.4089, -66.8920)
        )
        this.overlayKrig = new USGSOverlay(bounds, overlayUrl, this.$map)
      }
    },

    /**
     * Update the visualization based on query.
     */
    updateMap () {
      // Require map, shapes, records, query
      if (!this.ready) {
        return
      }

      var records = this.markersSource === 'txCenters' ? this.txRecords : this.hospitalRecords

      // This will update the map thanks to watchers
      let query = {
        organMode: this.queryOrganMode,
        regionMode: this.queryRegionMode,
        year: this.queryYear
      }
      let statId = this.queryStatisticSubId ? this.queryStatisticSubId : this.queryStatisticId
      this.aggregatedRecords = statFun['stat_' + statId](records, query)

      // TODO: Move to watchers?
      this.doOverlay()
    },

    /**
     * Update regions' colors.
     */
    updateRegionsStyle () {
      var colorScale = scaleLinear()
        .domain([this.legendMin, this.legendMax])
        .range(['yellow', 'red'])

      var overlayMode = this.overlayMode
      this.$map.data.setStyle(feature => {
        let value = this.aggregatedRecords[feature.getProperty(this.getGroupId())]
        let fillColor = overlayMode === 'k' ? '#fff' : (isNaN(value) ? colorScale(0) : colorScale(value))
        return {
          fillColor: fillColor,
          strokeWeight: 1
        }
      })
    },

    updateMinMax () {
      let min, max

      if (this.selectedStatistic.legend === 'percent') {
        // If statistic is a percentage/probability
        min = 0
        max = 1
      } else {
        // Use actual min and max values
        min = Number.MAX_SAFE_INTEGER
        max = Number.MIN_SAFE_INTEGER

        for (let k in this.aggregatedRecords) {
          if (isNaN(this.aggregatedRecords[k])) {
            continue
          }
          min = Math.min(this.aggregatedRecords[k], min)
          max = Math.max(this.aggregatedRecords[k], max)
        }
      }

      this.$store.commit('setLegendMinMax', { min, max })
    },

    /**
     * Draw GeoJSON shapes
     */
    updateShapes () {
      // Require map and shapes
      if (!this.readyMap || !this.readyShapes) {
        return
      }

      this.$map.data.forEach((feature) => {
        this.$map.data.remove(feature)
      })

      let geoJson
      switch (this.queryRegionMode) {
        case 'opo':
          geoJson = this.opoShapes
          break
        case 'state':
          geoJson = this.stateShapes
          break
        case 'unos':
          geoJson = this.unosShapes
      }

      this.$map.data.addGeoJson(geoJson)
      this.updateMap()
    }

  }
})
</script>



// WEBPACK FOOTER //
// src/components/MapDataLayer.vue